import { ThemeEvents } from '@theme/events';

/**
 * Remote control for a hidden variant option group in the product form.
 * Renders as an expandable dropdown that mirrors the hidden option group.
 */
class VariantPickerDropdown extends HTMLElement {
  selectors = {
    trigger: '[data-dropdown-trigger]',
    value: '[data-dropdown-value]',
    panel: '[data-dropdown-panel]',
    list: '[data-dropdown-list]',
    label: '[data-dropdown-label]',
    shopifySection: '.shopify-section',
    productInformationSection: '[id*="ProductInformation"]',
    productForm: 'product-form-component, form.shopify-product-form',
    variantOption: '.variant-picker__option[data-option-name]',
    radioInput: 'input[type="radio"]',
    variantSelect: 'select',
    variantSelectOption: 'option',
    variantOptionLabel: 'label',
    variantOptionLabelText: '.variant-option__button-label__text',
  };

  /** @type {HTMLElement | null} */
  #optionGroup = null;

  /** @type {MutationObserver | null} */
  #optionObserver = null;

  /** @type {HTMLButtonElement | null} */
  #trigger = null;

  /** @type {HTMLElement | null} */
  #valueEl = null;

  /** @type {HTMLElement | null} */
  #panel = null;

  /** @type {HTMLUListElement | null} */
  #list = null;

  /** @type {boolean} */
  #expanded = false;

  /** @type {Animation | undefined} */
  #animation;

  /** @type {number} */
  #animationSpeed = 300;

  connectedCallback() {
    this.#cacheElements();

    this.#trigger?.addEventListener('click', this.#toggle);
    document.addEventListener('click', this.#handleDocumentClick);

    this.#linkOptionGroup();
    this.#renderList();
    this.#observeOptionGroup();

    const scope = this.#getScope();
    scope?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    const scope = this.#getScope();
    scope?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    document.removeEventListener('click', this.#handleDocumentClick);
    this.#optionObserver?.disconnect();
    this.#optionObserver = null;
    this.#animation?.cancel();
  }

  #cacheElements() {
    this.#trigger = /** @type {HTMLButtonElement | null} */ (
      this.querySelector(this.selectors.trigger)
    );
    this.#valueEl = this.querySelector(this.selectors.value);
    this.#panel = this.querySelector(this.selectors.panel);
    this.#list = /** @type {HTMLUListElement | null} */ (this.querySelector(this.selectors.list));
  }

  #handleVariantUpdate = () => {
    this.#linkOptionGroup();
    this.#renderList();
    this.#observeOptionGroup();
  };

  #onOptionGroupChange = () => {
    this.#renderList();
  };

  #handleDocumentClick = (event) => {
    if (!this.#expanded) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#collapse();
  };

  #toggle = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.#expanded) {
      this.#collapse();
    } else {
      this.#expand();
    }
  };

  /**
   * @returns {HTMLElement | Document}
   */
  #getScope() {
    return (
      this.closest(this.selectors.shopifySection) ||
      this.closest(this.selectors.productInformationSection) ||
      document
    );
  }

  #linkOptionGroup() {
    const optionName = this.dataset.optionName;
    if (!optionName) {
      this.#optionGroup = null;
      return;
    }

    const scope = this.#getScope();
    const productForm = scope.querySelector(this.selectors.productForm);

    if (!productForm && scope !== document) {
      this.#optionGroup = null;
      return;
    }

    const searchRoot = productForm?.closest(this.selectors.shopifySection) || scope;
    const optionNameLower = optionName.toLowerCase();
    const match = Array.from(searchRoot.querySelectorAll(this.selectors.variantOption)).find(
      (element) =>
        element instanceof HTMLElement &&
        element.dataset.optionName?.toLowerCase() === optionNameLower
    );

    this.#optionGroup = match instanceof HTMLElement ? match : null;
  }

  #observeOptionGroup() {
    this.#optionObserver?.disconnect();
    this.#optionObserver = null;

    if (this.#optionGroup) {
      this.#optionGroup.removeEventListener('change', this.#onOptionGroupChange);
    }

    if (!this.#optionGroup) return;

    this.#optionGroup.addEventListener('change', this.#onOptionGroupChange);

    this.#optionObserver = new MutationObserver(this.#onOptionGroupChange);
    this.#optionObserver.observe(this.#optionGroup, {
      subtree: true,
      attributes: true,
      attributeFilter: [
        'checked',
        'data-current-checked',
        'data-option-available',
        'aria-disabled',
        'selected',
      ],
      childList: true,
    });
  }

  #renderList = () => {
    if (!this.#list || !this.#trigger) return;

    this.#list.replaceChildren();

    if (!this.#optionGroup) {
      this.#valueEl && (this.#valueEl.textContent = '');
      this.#trigger.disabled = true;
      this.#list.removeAttribute('role');
      this.#collapse({ animate: false });
      return;
    }

    const inputs = this.#optionGroup.querySelectorAll(this.selectors.radioInput);

    if (inputs.length) {
      inputs.forEach((input) => {
        if (this.#isInputSelected(input)) return;

        const item = this.#createListItem(input);
        if (item) this.#list.appendChild(item);
      });
    } else {
      const select = this.#optionGroup.querySelector(this.selectors.variantSelect);
      select?.querySelectorAll(this.selectors.variantSelectOption).forEach((option) => {
        if (!option.value || this.#isInputSelected(option, select)) return;

        const item = this.#createListItem(option, select);
        if (item) this.#list.appendChild(item);
      });
    }

    this.#trigger.disabled = !this.#optionGroup || this.#list.children.length === 0;
    this.#list.toggleAttribute('role', this.#list.children.length > 0);
    if (this.#list.children.length > 0) this.#list.setAttribute('role', 'listbox');
    this.#updateTriggerValue();

    if (this.#expanded) {
      this.#syncExpandedPanelHeight();
    } else {
      this.#collapse({ animate: false });
    }
  };

  #updateTriggerValue() {
    if (!this.#valueEl || !this.#optionGroup) return;

    const inputs = this.#optionGroup.querySelectorAll(this.selectors.radioInput);

    if (inputs.length) {
      const selected = Array.from(inputs).find((input) => this.#isInputSelected(input));

      if (selected instanceof HTMLInputElement) {
        const label = selected.closest(this.selectors.variantOptionLabel);
        const labelText = label
          ?.querySelector(this.selectors.variantOptionLabelText)
          ?.textContent?.trim();
        this.#valueEl.textContent = labelText || selected.value || '';
      }

      return;
    }

    const select = this.#optionGroup.querySelector(this.selectors.variantSelect);

    if (select?.selectedOptions[0]) {
      const option = select.selectedOptions[0];
      this.#valueEl.textContent = option.textContent?.trim() || option.value || '';
    }
  }

  #syncExpandedPanelHeight() {
    if (!this.#panel || !this.#expanded) return;

    this.#panel.style.overflow = 'hidden';
    this.#panel.style.height = `${this.#panel.scrollHeight}px`;
    this.#panel.style.overflow = '';
  }

  /**
   * @param {{ animate?: boolean }} [options]
   */
  #expand({ animate = true } = {}) {
    if (!this.#panel || !this.#list?.children.length || this.#expanded) return;

    this.#expanded = true;
    this.dataset.expanded = 'true';
    this.#trigger?.setAttribute('aria-expanded', 'true');

    const startHeight = this.#panel.offsetHeight;
    const endHeight = this.#panel.scrollHeight;

    if (!animate || startHeight === endHeight) {
      this.#panel.style.height = `${endHeight}px`;
      this.#panel.style.overflow = 'hidden';
      return;
    }

    this.#animatePanelHeight(startHeight, endHeight, () => {
      this.#panel.style.height = `${this.#panel.scrollHeight}px`;
      this.#panel.style.overflow = 'hidden';
    });
  }

  /**
   * @param {{ animate?: boolean }} [options]
   */
  #collapse({ animate = true } = {}) {
    if (!this.#panel) return;

    if (!this.#expanded) {
      this.#panel.style.height = '0px';
      this.#panel.style.overflow = 'hidden';
      return;
    }

    const startHeight = this.#panel.offsetHeight;

    this.#expanded = false;
    this.dataset.expanded = 'false';
    this.#trigger?.setAttribute('aria-expanded', 'false');

    const finishCollapse = () => {
      this.#panel.style.height = '0px';
      this.#panel.style.overflow = 'hidden';
    };

    if (!animate || startHeight === 0) {
      finishCollapse();
      return;
    }

    this.#animatePanelHeight(startHeight, 0, finishCollapse);
  }

  /**
   * @param {number} startHeight
   * @param {number} endHeight
   * @param {() => void} onFinish
   */
  #animatePanelHeight(startHeight, endHeight, onFinish) {
    if (!this.#panel) return;

    this.#panel.style.overflow = 'hidden';
    this.#panel.style.height = `${startHeight}px`;

    this.#animation?.cancel();
    this.#animation = this.#panel.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      {
        duration: this.#animationSpeed,
        easing: 'ease-in-out',
      }
    );

    this.#animation.onfinish = () => {
      onFinish();
    };
  }

  /**
   * @param {HTMLInputElement | HTMLOptionElement} input
   * @param {HTMLSelectElement | null} [select]
   * @returns {HTMLLIElement}
   */
  #createListItem(input, select = null) {
    const item = document.createElement('li');
    item.className = 'twp-variant-picker-dropdown__item';
    item.setAttribute('role', 'option');

    const label = input instanceof HTMLInputElement ? input.closest(this.selectors.variantOptionLabel) : null;
    const labelText = label
      ?.querySelector(this.selectors.variantOptionLabelText)
      ?.textContent?.trim();
    const value = labelText || input.value || input.textContent?.trim() || '';

    const labelEl = document.createElement('span');
    labelEl.className = 'twp-variant-picker-dropdown__label';
    labelEl.setAttribute('data-dropdown-label', '');
    labelEl.textContent = value;

    item.append(labelEl);

    const isAvailable = this.#isOptionAvailable(input);
    const isSelected = this.#isInputSelected(input, select);

    item.toggleAttribute('data-unavailable', !isAvailable);
    item.toggleAttribute('aria-selected', Boolean(isSelected));

    if (!isAvailable) {
      item.setAttribute('aria-disabled', 'true');
    }

    item.addEventListener('click', (event) => {
      event.stopPropagation();
      if (!this.#isOptionAvailable(input)) return;

      if (input instanceof HTMLInputElement) {
        input.click();
      } else if (select && input instanceof HTMLOptionElement) {
        select.value = input.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.#collapse();
    });

    return item;
  }

  /**
   * @param {HTMLInputElement | HTMLOptionElement} input
   * @param {HTMLSelectElement | null} [select]
   * @returns {boolean}
   */
  #isInputSelected(input, select = null) {
    if (input instanceof HTMLInputElement) {
      return input.checked || input.dataset.currentChecked === 'true';
    }

    return Boolean(select && select.value === input.value);
  }

  /**
   * @param {HTMLInputElement | HTMLOptionElement} input
   * @returns {boolean}
   */
  #isOptionAvailable(input) {
    if (input instanceof HTMLInputElement) {
      return (
        input.dataset.optionAvailable !== 'false' &&
        input.getAttribute('aria-disabled') !== 'true' &&
        !input.disabled
      );
    }

    return !input.disabled;
  }
}

if (!customElements.get('variant-picker-dropdown')) {
  customElements.define('variant-picker-dropdown', VariantPickerDropdown);
}
